import { LitElement, html, css } from "lit";
import { customElement, query, state } from "lit/decorators.js";
import { Operation, Rule, selectorsToString } from "../rules";
import { OutDesignOperatorsBoxElement } from "./OutDesignOperatorsBoxElement";
import { OutDesignRuleElement, getDefaultValueOperation } from "./OutDesignRuleElement";

import { isDebugModeOn } from "./utils";

import Sortable from 'sortablejs';

OutDesignRuleElement;
OutDesignOperatorsBoxElement;

@customElement("od-left-sidebar")
export class OutDesignLeftSidebar extends LitElement {
    
    @state() private rules: Rule[] = [];
    rulesSrc?: string;

    @query('.draggable')
    draggableList: HTMLElement;

    private sortable?: Sortable;
    
    static styles = css`
    :host {
        display: flex;
        flex-direction: column;
        font-family: sans-serif;
        overflow: inherit;
    }

    .no-rules {
        text-align: center;
        margin-top: 10em;
        height: 100%;
    }

    input::file-selector-button {
        margin-top: 1em;
        margin-left: 4em;
    }
    
    .rules {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow-y: scroll;
    }
    
    .rules od-rule:nth-child(even) {
        background-color: #fbfbfb;
    }

    .rules od-rule:not([editing]):hover {
      background-color: lightgrey;
    }

    .rules od-rule {
        transition:
          background-color .15s;
    }

    .add-rule-button {
        width: 100%;
        display: flex;
        justify-content: center;
        margin: 2em 0;
    }

    wa-button {
        text-align: center;
    }
    `;
    
    async firstUpdated() {
        if (isDebugModeOn()) {
            const json = JSON.parse(__RULES_DEBUG_JSON__);
            this.rules = [...json];
            this.rules.forEach(rule => {
                rule.activated = true;
            })
        }
        this.loadRulesFromLocalStorage()
    }
    
    // Inspired from https://lit.dev/playground/#gist=242f45fd2dbe21ecb6902f144686aae8
    updated() {
        // When there is no rules, then we have no sortable list.
        if(this.rules.length < 1) {
            this.sortable = undefined;
        } 
        if (!this.sortable && this.draggableList) {
            let before: Node | null;
            
            this.sortable = Sortable.create(this.draggableList, {
                animation: 150,
                ghostClass: 'ghost',
                onStart: (e) => {
                    before = e.item.previousSibling;
                },
                onEnd: (e) => {
                    if (before && before.parentNode) {
                        (before as any).after(e.item);
                    }
                    
                    this.rules.splice(
                        e.newIndex as number,
                        0,
                        this.rules.splice(e.oldIndex as number, 1)[0]
                    );
                    
                    this.requestUpdate();
                },
            });
        }
    }

    private loadRules (elem : HTMLInputElement) {
        new Response(elem.files?.[0]).json().then(json => {
            this.rules = [...json];
            this.emitRules()
        }, err => {
            // not json
        })
    }

    emitRules(changes: boolean = true) {
        this.dispatchEvent(
            new CustomEvent( changes ? "rules-changed" : "rules-unchanged", {
                detail: this.rules.filter(rule => rule.activated),
                bubbles: true,
                composed: true
            })
        );
        this.saveRulesToLocalStorage()
    }
    
    connectedCallback() {
        super.connectedCallback();
        document.addEventListener("json-loaded", this.handleFileSelectedFromHeader);
        document.addEventListener("json-loaded-from-sample", this.handleSampleSelected);
        document.addEventListener("rules-request", this.handleRulesRequest);
        document.addEventListener("emit-operator", this.handleOperator);
        document.addEventListener("new-rule", this.handleNewRule);
        document.addEventListener("delete-rules", this.deleteRules);
        document.addEventListener("export-rules", this.exportRules);
    }
    
    disconnectedCallback() {
        document.removeEventListener("json-loaded", this.handleFileSelectedFromHeader);
        document.removeEventListener("json-loaded-from-sample", this.handleSampleSelected);
        document.removeEventListener("rules-request", this.handleRulesRequest);
        document.removeEventListener("emit-operator", this.handleOperator);
        document.removeEventListener("new-rule", this.handleNewRule);
        document.removeEventListener("delete-rules", this.deleteRules);
        document.removeEventListener("export-rules", this.exportRules);
        super.disconnectedCallback();
    }
    
    private handleRulesRequest = (e : Event) => {
        const ev = e as CustomEvent;
        this.emitRules(ev.detail.changes)
    };

    private handleNewRule = (e: Event) => {
        const ev = e as CustomEvent;
        this.addRule(selectorsToString(ev.detail.selectors), ev.detail.operator);
    }

    private deleteRules = () => {
        this.rules = []
        this.emitRules()
    }

    private exportRules = async () => {
        if (!this.rules) return;
        const clonedRules = this.rules.map(({ selector, operation }) => ({
        selector,
        operation,
        }));

        const filename = "rules.json"

        let blob: Blob;
        const text = JSON.stringify(clonedRules, null, 2)
        blob = new Blob([text as string], { type: "text/plain;charset=utf-8" });

        const url = URL.createObjectURL(blob);

        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();

        // cleanup
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    private handleOperator = (e: Event) => {
        const ev = e as CustomEvent<string>;
        // Check if a rule is already being edited!
        if(this.rules.filter(rule => rule.editing).length > 0 ) {
            this.propagateOperator(ev.detail)
        }
        // and if not, send an event to the SelectorElement
        document.dispatchEvent(new CustomEvent("new-rule-with-operator", {
            detail: ev.detail,
            bubbles: true,
            composed: true
        }));
    }

    private propagateOperator(operator: string) {
        const ruleEl = this.renderRoot.querySelector("od-rule[editing]");
        ruleEl?.dispatchEvent(new CustomEvent("propagate-operator", {
            detail: operator,
            bubbles: true,
            composed: true
        }));
    }

    async addRule(sel?: string, operator?: keyof Operation) {
        // Check if a rule is already being edited!
        if(this.rules.filter(rule => rule.editing).length > 0 ) return
        let operation: Operation = {};
        if(operator) {
            operation[operator] = getDefaultValueOperation(operator) as any
        } else operation = {}

        const newRule: Rule = {
            selector: sel ?? "",
            operation: operation,
            editing: true,
            activated: true
        };

        this.rules = [...this.rules, newRule];
        await this.updateComplete;

        const rulesElement = this.renderRoot.querySelector(".rules")
        rulesElement?.scrollTo(0, rulesElement?.scrollHeight);
    }
    
    addRules(rules: Rule[]) {
        this.rules = [...this.rules, ...rules];
    }

    updateRule(index: number, updated: Rule) {
        const copy = [...this.rules];
        copy[index] = updated;
        this.rules = copy;
        this.emitRules();
    }

    deleteRule(index: number) {
        const copy = [...this.rules];
        copy.splice(index, 1);
        this.rules = copy;
        this.emitRules();
    }

    private handleFileSelectedFromHeader = (e: Event) => {
        const ev = e as CustomEvent<HTMLInputElement>;
        const files = ev.detail
        this.loadRules(files)
    };

    private handleSampleSelected = (e: Event) => {
        const ev = e as CustomEvent;
        this.rules = ev.detail;
        this.rules.forEach(rule => {
            rule.activated = true;
        })
    };

    private handleFileSelectedFromLeftSidebar(event: Event) {
        const files = event.target as HTMLInputElement;
        this.loadRules(files)
    }

    private saveRulesToLocalStorage() {
        localStorage.setItem("rules", JSON.stringify(this.rules))
    }

    private loadRulesFromLocalStorage() {
        if(localStorage.getItem("rules")) this.rules = JSON.parse(localStorage.getItem("rules") as string)
    }

    renderRules() {
        return html`
        <div class="rules draggable">
            ${this.rules.map(
            (rule, i) => html`
                <od-rule
                .rule=${rule}
                .editing=${rule.editing}
                .activated=${rule.activated}
                .open=${rule.editing}
                .index=${i}
                @rule-updated=${(e: CustomEvent) => this.updateRule(i, e.detail)}
                @rule-deleted=${(e: CustomEvent) => this.deleteRule(i)}
                ></od-rule>
            `
            )}
        </div>
        `;
    }

    renderMissingRules() {
        return html`
        <p class="no-rules">
            <label for="idml">No rules to apply.<br />Upload or add some.</label>
            <br/>
            <input
            id="idml"
            type="file"
            accept=".json"
            @change=${this.handleFileSelectedFromLeftSidebar}
            />
        </p>
        `;
    }

    render() {
        let rules;
        if(this.rules.length > 0) {
            rules = this.renderRules();
        } 
        else {
            rules = this.renderMissingRules();
        }
        return [rules, html`<od-operators-box></od-operators-box>`]
    }
}